import { createTheme } from '@mui/material/styles';

const sharedConfig = {
  typography: {
    fontFamily: [
      'Verdana',
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
    ].join(','),
    h1: {
      fontSize: '2.25rem', // 36px
      fontWeight: 600,
      lineHeight: 1.25,
      color: '#313246',
    },
    h2: {
      fontSize: '1.5rem', // 24px
      fontWeight: 600,
      lineHeight: 1.375,
      color: '#313246',
    },
    h3: {
      fontSize: '1.25rem', // 20px
      fontWeight: 400,
      lineHeight: 1.375,
      color: '#313246',
    },
    h4: {
      fontSize: '1.125rem', // 18px
      fontWeight: 500,
      lineHeight: 1.5,
      color: '#313246',
    },
    h5: {
      fontSize: '1rem', // 16px
      fontWeight: 500,
      lineHeight: 1.5,
      color: '#313246',
    },
    h6: {
      fontSize: '0.875rem', // 14px
      fontWeight: 500,
      lineHeight: 1.5,
      color: '#313246',
    },
    body1: {
      fontSize: '1rem', // 16px
      fontWeight: 200,
      lineHeight: 1.5,
      color: '#313246',
    },
    body2: {
      fontSize: '0.875rem', // 14px
      fontWeight: 200,
      lineHeight: 1.5,
      color: '#6b7280',
    },
    button: {
      fontSize: '0.875rem', // 14px
      fontWeight: 500,
      lineHeight: 1.5,
      textTransform: 'none', // Disable uppercase transformation
    },
  },
  spacing: 8, // 8px base spacing unit
  shape: {
    borderRadius: 8, // 8px default border radius
  },
};

const sharedPalette = {
  secondary: {
    main: '#63cc6c', // $secondary
    dark: '#54b85a', // $secondary-dark
    light: '#72d07a', // $secondary-light
    contrastText: '#ffffff',
  },
  error: {
    main: '#f74e1c', // $error
    light: '#f96b4a',
    dark: '#d63e0f',
    contrastText: '#ffffff',
  },
  warning: {
    main: '#f9aa55', // $warning
    light: '#fbbd7a',
    dark: '#f79730',
    contrastText: '#ffffff',
  },
  info: {
    main: '#58b0c3', // $info
    light: '#6bb8c8',
    dark: '#4a9ba8',
    contrastText: '#ffffff',
  },
  success: {
    main: '#63cc6c', // $success
    light: '#72d07a',
    dark: '#54b85a',
    contrastText: '#ffffff',
  },
  grey: {
    50: '#f9fafb', // $gray-100
    100: '#f5f7f8', // $gray-200
    200: '#edf0f2', // $gray-300
    300: '#cacbd3', // $gray-400
    400: '#9ca3af', // $gray-500
    500: '#6b7280', // $gray-600
    600: '#4b5563', // Custom
    700: '#374151', // Custom
    800: '#1f2937', // Custom
    900: '#313246', // $gray-900
  },
  text: {
    primary: '#313246', // $text-primary
    secondary: '#6b7280', // $text-secondary
    disabled: '#cacbd3', // $text-disabled
  },
  background: {
    default: '#ffffff', // $background-default
    paper: '#ffffff', // $background-paper
  },
  divider: '#edf0f2', // $border-light
};

const sharedComponentOverrides = {
  MuiButton: {
    styleOverrides: {
      root: {
        borderRadius: 8,
        textTransform: 'none',
        fontWeight: 500,
        padding: '8px 16px',
      },
      contained: {
        boxShadow: 'none',
        '&:hover': {
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
        },
      },
    },
  },
  MuiTextField: {
    styleOverrides: {
      root: {
        '& .MuiOutlinedInput-root': {
          borderRadius: 8,
        },
      },
    },
  },
  MuiCard: {
    styleOverrides: {
      root: {
        borderRadius: 12,
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
      },
    },
  },
  MuiPaper: {
    styleOverrides: {
      root: {
        borderRadius: 8,
      },
    },
  },
  MuiChip: {
    styleOverrides: {
      root: {
        borderRadius: 16,
      },
    },
  },
  MuiTooltip: {
    styleOverrides: {
      tooltip: {
        backgroundColor: '#f5f5f9',
        color: 'rgba(0, 0, 0, 0.87)',
        maxWidth: 250,
        border: '1px solid #dadde9',
        borderRadius: 8,
      },
    },
  },
};

export const earthTheme = createTheme({
  ...sharedConfig,
  palette: {
    ...sharedPalette,
    primary: {
      main: '#c9a478', // $primary – light warm beige/tan
      dark: '#b08a5a', // $primary-dark
      light: '#d8ba96', // $primary-light
      lighter: '#e5ccb0', // $primary-lighter
      contrastText: '#ffffff',
    },
  },
  components: {
    ...sharedComponentOverrides,
    MuiDataGrid: {
      styleOverrides: {
        root: {
          border: 'none',
          '& .MuiDataGrid-row:nth-of-type(even)': {
            backgroundColor: 'rgba(201,164,120,0.05)',
            '&:hover': {
              backgroundColor: 'rgba(201,164,120,0.12)',
            },
          },
          '& .MuiDataGrid-row:nth-of-type(odd)': {
            backgroundColor: 'transparent',
            '&:hover': {
              backgroundColor: 'rgba(201,164,120,0.08)',
            },
          },
          '& .MuiDataGrid-cell': {
            borderBottom: '1px solid #edf0f2', // $border-light
          },
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: 'rgba(201,164,120,0.14)',
            borderBottom: '2px solid rgba(201,164,120,0.35)',
          },
          '& .MuiDataGrid-columnHeader': {
            fontWeight: 600,
            color: '#313246',
          },
          '& .MuiDataGrid-footerContainer': {
            borderTop: '1px solid rgba(201,164,120,0.25)',
            backgroundColor: 'rgba(201,164,120,0.08)',
          },
        },
      },
    },
  },
});

export const oceanTheme = createTheme({
  ...sharedConfig,
  palette: {
    ...sharedPalette,
    primary: {
      main: '#2e6e8e',
      dark: '#1f5470',
      light: '#4185a8',
      lighter: '#5a9dbd',
      contrastText: '#ffffff',
    },
  },
  components: {
    ...sharedComponentOverrides,
    MuiDataGrid: {
      styleOverrides: {
        root: {
          border: 'none',
          '& .MuiDataGrid-row:nth-of-type(even)': {
            backgroundColor: 'rgba(46,110,142,0.05)',
            '&:hover': {
              backgroundColor: 'rgba(46,110,142,0.12)',
            },
          },
          '& .MuiDataGrid-row:nth-of-type(odd)': {
            backgroundColor: 'transparent',
            '&:hover': {
              backgroundColor: 'rgba(46,110,142,0.08)',
            },
          },
          '& .MuiDataGrid-cell': {
            borderBottom: '1px solid #edf0f2',
          },
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: 'rgba(46,110,142,0.12)',
            borderBottom: '2px solid rgba(46,110,142,0.3)',
          },
          '& .MuiDataGrid-columnHeader': {
            fontWeight: 600,
            color: '#313246',
          },
          '& .MuiDataGrid-footerContainer': {
            borderTop: '1px solid rgba(46,110,142,0.2)',
            backgroundColor: 'rgba(46,110,142,0.07)',
          },
        },
      },
    },
  },
});

const darkTypography = {
  ...Object.fromEntries(
    ['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'body1'].map((k) => [k, { ...sharedConfig.typography[k], color: '#e2e8f0' }]),
  ),
  subtitle1: { color: '#e2e8f0' },
  subtitle2: { color: '#94a3b8' },
  body2: { ...sharedConfig.typography.body2, color: '#94a3b8' },
};

export const darkTheme = createTheme({
  ...sharedConfig,
  typography: {
    ...sharedConfig.typography,
    ...darkTypography,
    body2: { ...sharedConfig.typography.body2, color: '#94a3b8' },
  },
  palette: {
    mode: 'dark',
    primary: {
      main: '#7c6fcd',
      dark: '#6357b8',
      light: '#9589d6',
      contrastText: '#ffffff',
    },
    secondary: sharedPalette.secondary,
    error: sharedPalette.error,
    warning: sharedPalette.warning,
    info: sharedPalette.info,
    success: sharedPalette.success,
    text: {
      primary: '#e2e8f0',
      secondary: '#94a3b8',
      disabled: '#475569',
    },
    background: {
      default: '#0f1117',
      paper: '#1a1b2e',
    },
    divider: '#2d3748',
  },
  components: {
    ...sharedComponentOverrides,
    MuiFormLabel: {
      styleOverrides: {
        root: {
          color: '#b0bec5',
          '&.Mui-focused': {
            color: '#7c6fcd',
          },
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        input: {
          color: '#e2e8f0',
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: '#2d2f45',
          color: '#e2e8f0',
          maxWidth: 250,
          border: '1px solid #3d3f5a',
          borderRadius: 8,
        },
      },
    },
    MuiDataGrid: {
      styleOverrides: {
        root: {
          border: 'none',
          '& .MuiDataGrid-row:nth-of-type(even)': {
            backgroundColor: 'rgba(124,111,205,0.08)',
            '&:hover': { backgroundColor: 'rgba(124,111,205,0.18)' },
          },
          '& .MuiDataGrid-row:nth-of-type(odd)': {
            backgroundColor: 'transparent',
            '&:hover': { backgroundColor: 'rgba(124,111,205,0.12)' },
          },
          '& .MuiDataGrid-cell': {
            borderBottom: '1px solid #2d3748',
          },
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: 'rgba(124,111,205,0.18)',
            borderBottom: '2px solid rgba(124,111,205,0.4)',
          },
          '& .MuiDataGrid-columnHeader': {
            fontWeight: 600,
            color: '#e2e8f0',
          },
          '& .MuiDataGrid-footerContainer': {
            borderTop: '1px solid rgba(124,111,205,0.25)',
            backgroundColor: 'rgba(124,111,205,0.1)',
          },
        },
      },
    },
  },
});

export const themes = {
  earth: earthTheme,
  ocean: oceanTheme,
  dark: darkTheme,
};

export default earthTheme;
